import React from 'react'
import Pt from 'prop-types'
import { connect } from 'react-redux'
import Immutable from 'immutable'
import Ipt from 'react-immutable-proptypes'
import {
  Col,
  FormControl, HelpBlock,
  Radio,
  Row,
  Table,
} from 'react-bootstrap'

import {
  FormField,
} from 'internal-components/src/components/Form'

import {
  OfferFilterDataRecord,
  SignupWithExternalsRecord,
} from 'internal-components/src/records'

import { makeSelectOffers } from 'internal-components/src/redux/modules/product/selectors'
import { format as formatCurrency } from 'internal-components/src/util/Currency'
import { createError } from "internal-components/src/records";

import {
  selectErrorsByPath,
  selectEditSignupWithExternals,
} from '../../../redux/modules/signupEdit/selectors'

import * as signupActions from '../../../redux/modules/signupEdit/actions'

import './ServiceOptions.css'

const calixRouterOptions = [
  { id: 'router', name: 'Gigaspire Wi-Fi System' },
  { id: 'router_1_wap', name: 'Gigaspire Wi-Fi System + 1 Wi-Fi Extender' },
  { id: 'router_2_wap', name: 'Gigaspire Wi-Fi System + 2 Wi-Fi Extenders' },
];
const calixContractOptions = [
  { value: false, name: 'Up front' },
  { value: true, name: '24 months' },
];


const NF18MESH = 'nbn.nf18mesh.v2';
const NF20MESH = 'nbn.nf20mesh.v1';
const VX230V = 'modem.vx230v.v1';
const MR70X  = 'modem.mr70x.v1';

function empty(val) { return val === '0' || !val; }

function shouldShowModemOptions(product, offer) {
  return product.get('type') === 'NBN' && product.get('name') !== 'NBN (WIFI)' ||
         product.get('type') === 'VDSL';
}

function hasCalixData(signup) {
  return signup.data.some((v, k) => k.includes('calix') && v === '1');
}

function calixDslModemRequired(product, offer, signup, qualificationResults) {
  if (!offer.attributes.includes('calix') && !hasCalixData(signup)) {
    return false;
  }

  if (product.type === 'VDSL') {
    if (offer.upgradeGroup.match(/GFAST$/)) {
      return false;
    } else {
      return true;
    }
  }

  if (!qualificationResults || qualificationResults.getIn([9, 'restrictions', 'speedLimitReason']) === 'no-fibre') {
    return true;
  }

  return false;
}

function shouldShowPhoneAddon(product, offer, signup) {
  if (offer.attributes.includes('calix')) {
    return false;
  }

  return product.get('type') === 'NBN' && product.get('name') !== 'NBN (WIFI)' ||
         product.get('type') === 'VDSL' && signup.data.get(MR70X) !== '1';
}

function canPortPhoneNumber(product) {
  return product.options.filter(option => option.dataKey === 'voip.existing_phone_number').size > 0;
}

function handsetOptionName(product, offer) {
  if (offer.get('uniqueId') === 'BUSPHONE-SOFT') {
    return null;
  }
  if (offer.get('uniqueId') === 'BUSPHONE-FLEX') {
    return 'voip.v62g.v1';
  }
  if (offer.get('uniqueId') === 'BUSPHONE-ELEV') {
    return 'voip.v66.v1';
  }
  return 'voip.phone_handset';
}

export function shouldShowOptions(product, offer, signup) {
  return shouldShowModemOptions(product, offer) || shouldShowPhoneAddon(product, offer, signup) || product.get('type') === 'PHONE';
}

function supportsMR70X(product, offer, qualificationResults) {
  return false; // Temporarily disabled

  const nbnFibreAvailable = !qualificationResults || qualificationResults.getIn([9, 'restrictions', 'speedLimitReason']) !== 'no-fibre';
  const matches = offer.speed.match(/^Up to (\d+)/);
  const speed = matches ? parseFloat(matches[1]) : 0;
  return product.get('type') === 'NBN' && product.get('name') !== 'NBN (WIFI)' && nbnFibreAvailable ||
    product.get('type') === 'VDSL' && speed >= 250;
}

export function handleSubmitOptions(signupWithExternals, signupEditMeta, setData, setExtraOfferId) {
  const {product, signup, offer} = signupWithExternals;
  if (shouldShowPhoneAddon(product, offer, signup)) {
    // If the user has deselected the option for "voice", then we need to
    // deselect the hidden options about a phone handset etc.
    if (!signupEditMeta.get('needsVoice')) {
      setExtraOfferId(0); // Must be a non-null falsy value
      setData('voip.phone_handset', '0');
      setData('voip.existing_phone_number', '');
    }
  }
  if ((shouldShowPhoneAddon(product, offer, signup) && signupEditMeta.get('needsVoice')) || canPortPhoneNumber(product)) {
    // If "use existing number" was unchecked, blank out the existing number field.
    if (!signupEditMeta.get('useExistingNumber')) {
      setData('voip.existing_phone_number', '');
    }
  }
}

export class ServiceOptions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {clientSideErrors: Immutable.Map()};

    const { signup, offer, extraOffer, product } = props.signupWithExternals;

    // For nbn and voice signups, this page has some radio buttons that are not
    // saved to the database. These options are calculated when the page is
    // opened based on the user's other answers, then saved to signupEdit.meta.

    if (shouldShowPhoneAddon(product, offer, signup)) {
      props.setNeedsVoice(!empty(extraOffer));
    }
    if (shouldShowPhoneAddon(product, offer, signup) || canPortPhoneNumber(product)) {
      props.setUseExistingNumber(!empty(signup.data.get('voip.existing_phone_number')));
    }
    if (offer.attributes.has('price_by_extensions') && !signup.data.get('phone-ext-count')) {
      props.setData('phone-ext-count', 1);
    }
    if (offer.attributes.has('calix') || hasCalixData(signup)) {
      let calixRouter = 'router';
      let calixContract = false;
      signup.data.forEach((v, k) => {
        const match = k.match(/^calix\.(router\w*)\.(\w*contract)\.\w+?$/);
        if (match && parseFloat(v)) {
          calixRouter = match[1];
          calixContract = match[1] === 'contract';
          return false;
        }
      });
      this.selectCalixOption(calixRouter, calixContract, true);
      this.state = Object.assign(this.state, {calixRouter, calixContract});
    }

    // Doing some client-side validation because it's easier than duplicating the logic in Invocation.
    if (props.receiveDoValidate) {
      props.receiveDoValidate(() => this.validate());
    }
  }

  validate() {
    let errors = Immutable.Map();
    const { signup, product, offer } = this.props.signupWithExternals;
    let valid = true;

    // This block is a copy of the logic for rendering the analogue/VOIP option.
    if ((shouldShowPhoneAddon(product, offer, signup) && this.props.signupEditMeta.get('needsVoice')) || product.type === 'PHONE') {
      const hon = handsetOptionName(product, offer);
      const phoneHandsetOption = product.options.filter(option => option.dataKey === hon).first();
      if (!offer.attributes.includes('price_by_extensions') && phoneHandsetOption && empty(signup.data.get(phoneHandsetOption.dataKey))) {
        // The analogue/VOIP option was rendered; validate it.
        if (!signup.data.get('voip.existing_phone_type')) {
          valid = false;
          errors = errors.set('voip.existing_phone_type', Immutable.Set([
            createError({message: 'Please select a phone type'}),
          ]));
        }
      }
    }

    this.setState({clientSideErrors: errors});

    return valid;
  }

  selectCalixOption(router, contract, isConstructor) {
    const { offer, product } = this.props.signupWithExternals;
    const qualificationResults = this.props.signupEditMeta.getIn(['qualificationResults']);

    if (!isConstructor) {
      this.setState({calixRouter: router, calixContract: contract});
    }

    // Clear any extant data keys starting with "calix", then add the correct ones.
    const { signup } = this.props.signupWithExternals;

    signup.data.forEach((v, k) => {
      if (k.match(/^calix\./)) {
        this.props.setData(k, '0');
      }
    });

    this.props.setData(`calix.${router}.${contract ? 'contract' : 'no_contract'}.v1`, '1');
    if (calixDslModemRequired(product, offer, signup, qualificationResults)) {
      this.props.setData(`calix.dsl_modem.${contract ? 'contract' : 'no_contract'}.v1`, '1');
    }
  }

  render () {
    const { signup, product, offer } = this.props.signupWithExternals;
    const qualificationResults = this.props.signupEditMeta.getIn(['qualificationResults']);
    const that = this;

    const renderedOptions = [];

    if (shouldShowModemOptions(product, offer) && hasCalixData(signup) && this.state && this.state.calixContract !== undefined) {
      const isFullCalixOffer = offer.attributes.includes('calix');
      const dslModemRequired = calixDslModemRequired(product, offer, signup, qualificationResults);
      const selectedCalixOptions = signup.data.filter((v, k) => parseFloat(v) && k.match(/^calix\./));
      let displayPrice = 0, priceDisplayType;
      if (this.state.calixContract) {
        selectedCalixOptions.forEach((v, k) => {
          displayPrice += product.options.filter(option => option.dataKey === k).first().recurringPrice;
        });
        priceDisplayType = 'recurring';
      } else {
        selectedCalixOptions.forEach((v, k) => {
          displayPrice += product.options.filter(option => option.dataKey === k).first().setupPrice;
        });
        priceDisplayType = 'setup';
      }
      renderedOptions.push(<div key="modem">
        <h3>{isFullCalixOffer ? 'Infinite SmartLife System' : 'Gigaspire u4.2 system'}</h3>
        {isFullCalixOffer && (
          <p>The Infinite SmartLife System comes with a Gigaspire u4.2 System and the Infinite eXperience mobile app.</p>
        )}
        <Row>
          <Col md={5}>
            {calixRouterOptions.map((option) => {
              const key = `calix.${option.id}.${this.state.calixContract ? 'contract' : 'no_contract'}.v1`;
              return <Radio
                key={option.id}
                checked={!empty(signup.data.get(key))}
                onChange={() => this.selectCalixOption(option.id, this.state.calixContract)}>
                {option.name}{dslModemRequired ? ' + DSL Modem' : ''}
              </Radio>;
            })}
          </Col>
          <Col md={2}>
            <Radio
              checked={!this.state.calixContract}
              onChange={() => this.selectCalixOption(this.state.calixRouter, false)}>
              Up Front
            </Radio>
            {isFullCalixOffer && (
              <Radio
                checked={this.state.calixContract}
                onChange={() => this.selectCalixOption(this.state.calixRouter, true)}>
                24 month contract
              </Radio>
            )}
          </Col>
          <Col md={3}>
            <div className="calix-price-display">
              <span className="amount">
                ${displayPrice.toFixed(0)}
              </span>
              <span className="subtype">
                {priceDisplayType === 'recurring' && '/month'}
                {priceDisplayType === 'setup' && ' setup'}
              </span>{' '}
              <span className="tax">inc GST</span>
            </div>
          </Col>
        </Row>
      </div>);
    }

    if (shouldShowModemOptions(product, offer) && !hasCalixData(signup)) {
      const vx230vOption = product.options.filter(option => option.dataKey === VX230V).first();
      const mr70xOption  = product.options.filter(option => option.dataKey === MR70X ).first();

      function setSelectedModem(modem) {
        that.props.setData(NF18MESH, modem === NF18MESH ? '1' : '0');
        that.props.setData(NF20MESH, modem === NF20MESH ? '1' : '0');
        that.props.setData(VX230V, modem === VX230V ? '1' : '0');
        that.props.setData(MR70X,  modem === MR70X  ? '1' : '0');
      }

      renderedOptions.push(<div key="modem">
        <h3>Do you need a modem?</h3>
        <Row>
          {supportsMR70X(product, offer, qualificationResults) &&
            <Col md={4}>
              <p>
                If you require a router for home and don't need phone ports then we recommend the{' '}
                <strong>Mercusys MR70X</strong>. We can send you an MR70X for <strong>${mr70xOption.setupPrice}</strong>{' '}
                including delivery, pre-configured so all you need to do is plug it in.</p>
            </Col>}
          <Col md={4}>
            <p>
              If you require a router for your business with Wi-Fi 6 we recommend the <strong>TP-Link VX230v</strong>.
              We can send you a VX230v for <strong>${vx230vOption.setupPrice}</strong> including delivery,
              pre-configured so all you need to do is plug it in.</p>
          </Col>
        </Row>
        <Row>
          {supportsMR70X(product, offer, qualificationResults) && <Col md={4}>
            <Radio
              checked={!empty(signup.data.get(MR70X))}
              onChange={() => setSelectedModem(MR70X)}>
              Send me an MR70X (${mr70xOption.setupPrice})
            </Radio>
          </Col>}
          <Col md={4}>
            <Radio
              checked={!empty(signup.data.get(VX230V))}
              onChange={() => setSelectedModem(VX230V)}>
              Send me a VX230v (${vx230vOption.setupPrice})
            </Radio>
          </Col>
          <Col md={4}>
            <Radio
              checked={empty(signup.data.get(NF18MESH)) && empty(signup.data.get(NF20MESH)) &&
                empty(signup.data.get(MR70X)) && empty(signup.data.get(VX230V))}
              onChange={() => setSelectedModem(null)}>
              No thanks ($0)
            </Radio>
          </Col>
        </Row>
        <hr/>
      </div>)
    }

    if (shouldShowPhoneAddon(product, offer, signup)) {
      const isVoiceSelected = this.props.signupEditMeta.get('needsVoice');

      renderedOptions.push(<div key="voice-needed">
        <h3>Do you need phone service?</h3>
        <Row>
          <Col md={4}>
            <p>Do you need to make phone calls over your internet connection? This technology, known as VoIP, is the modern version of conventional telephone service.</p>
          </Col>
          <Col md={1}>
            <Radio
              checked={isVoiceSelected}
              onChange={() => this.props.setNeedsVoice(true)}>
              Yes
            </Radio>
            <Radio
              checked={!isVoiceSelected}
              onChange={() => this.props.setNeedsVoice(false)}>
              No
            </Radio>
          </Col>
        </Row>
      </div>)

      if (isVoiceSelected) {
        renderedOptions.push(<div key="voip-plan">
          <h3>Select a phone plan</h3>

          <Table bordered hover className="extra-offer-selector">
            <thead>
              <tr>
                <th></th>
                <th>Setup Price</th>
                <th>Monthly Price</th>
                <th>Features</th>
              </tr>
            </thead>
            <tbody>
              {this.props.voipOffers.map(voipOffer =>
                <tr key={'voip-offer-'+voipOffer.id}>
                  <td>
                    <Radio
                      checked={signup.extraOffer === voipOffer.id}
                      onChange={() => this.props.setExtraOfferId(voipOffer.id)}
                    >
                      {voipOffer.name}
                    </Radio>
                  </td>
                  <td>
                    {formatCurrency(voipOffer.setupAmount)}
                  </td>
                  <td>
                    {formatCurrency(voipOffer.recurringAmount)}
                  </td>
                  <td>
                    {voipOffer.features.join(' ')}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>)
      }
    }

    if ((shouldShowPhoneAddon(product, offer, signup) && this.props.signupEditMeta.get('needsVoice')) || product.type === 'PHONE') {
      const hon = handsetOptionName(product, offer);
      const phoneHandsetOption = product.options.filter(option => option.dataKey === hon).first();
      const isExistingNumberSelected = this.props.signupEditMeta.get('useExistingNumber');

      if (offer.attributes.has('price_by_extensions')) {
        renderedOptions.push(<div key="number-of-extensions">
          <h3>Phone Extensions</h3>
          <Row>
            <Col md={4}>
              <FormField
                bsSize="medium"
                controlLabel="Number of Extensions"
                required={true}
                errors={this.props.errors}
                errorKey="data[phone-ext-count]"
              >
                <FormControl
                  onChange={e => {
                    let number = e.target.value;
                    if (number !== '') {
                      number = Math.floor(parseFloat(number));
                      if (Number.isNaN(number) || number < 1) {
                        number = 1;
                      }
                      if (number > 100) {
                        number = 100;
                      }
                    }
                    this.props.setData('phone-ext-count', number);
                  }}
                  type="text"
                  value={signup.data.get('phone-ext-count') || ''}
                />
              </FormField>
              <HelpBlock>How many phone extensions do you need?</HelpBlock>
            </Col>
          </Row>
        </div>);
      }

      if (phoneHandsetOption && phoneHandsetOption.dataKey === 'voip.phone_handset') {
        renderedOptions.push(<div key="phone-handset">
          <h3>Phone Handset</h3>

          <Row>
            <Col md={4}>
              <p>We recommend the Grandstream GXP1628. We can send you a Grandstream GXP1628 for ${phoneHandsetOption.setupPrice} including delivery, pre-configured so all you need to do is plug it in.</p>
            </Col>
            <Col md={1}>
              <Radio
                checked={!empty(signup.data.get('voip.phone_handset'))}
                onChange={() => this.props.setData('voip.phone_handset', '1')}>
                Yes
              </Radio>
              <Radio
                checked={empty(signup.data.get('voip.phone_handset'))}
                onChange={() => this.props.setData('voip.phone_handset', '0')}>
                No
              </Radio>
            </Col>
          </Row>
        </div>)
      }

      if (phoneHandsetOption && phoneHandsetOption.dataKey === 'voip.v62g.v1') {
        renderedOptions.push(<div key="phone-handset">
          <h3>Phone Handset</h3>

          <Row>
            <Col md={4}>
              <p>
                Your plan includes a Fanvil V62G for each extension. This is free under a 24-month contract.
                Alternatively, you can buy the Fanvils up front for ${phoneHandsetOption.setupPrice} each and not be under a contract.
              </p>
            </Col>
            <Col md={4}>
              <Radio
                checked={!empty(signup.data.get('voip.v62g.v1'))}
                onChange={() => this.props.setData('voip.v62g.v1', '1')}>
                No contract (pay for the Fanvils up front)
              </Radio>
              <Radio
                checked={empty(signup.data.get('voip.v62g.v1'))}
                onChange={() => this.props.setData('voip.v62g.v1', '0')}>
                24-month contract (free)
              </Radio>
            </Col>
          </Row>
        </div>)
      }

      if (phoneHandsetOption && phoneHandsetOption.dataKey === 'voip.v66.v1') {
        renderedOptions.push(<div key="phone-handset">
          <h3>Phone Handset</h3>

          <Row>
            <Col md={4}>
              <p>
                Your plan includes a Fanvil V66 Pro for each extension. This is free under a 24-month contract.
                Alternatively, you can buy the Fanvils up front for ${phoneHandsetOption.setupPrice} each and not be under a contract.
              </p>
            </Col>
            <Col md={1}>
              <Radio
                checked={!empty(signup.data.get('voip.v66.v1'))}
                onChange={() => this.props.setData('voip.v66.v1', '1')}>
                Yes
              </Radio>
              <Radio
                checked={empty(signup.data.get('voip.v66.v1'))}
                onChange={() => this.props.setData('voip.v66.v1', '0')}>
                No
              </Radio>
            </Col>
          </Row>
        </div>)
      }

      if (canPortPhoneNumber(product)) {
        renderedOptions.push(<div key="new-or-existing-number">
          <h3>Do you want to keep your current phone number?</h3>

          <Row>
            <Col md={4}>
              <p>Do you have an existing phone number you want to keep? We can bring it across to your new service with
                the cooperation of your current provider.</p>
            </Col>
            <Col md={1}>
              <Radio
                checked={isExistingNumberSelected}
                onChange={() => this.props.setUseExistingNumber(true)}>
                Yes
              </Radio>
              <Radio
                checked={!isExistingNumberSelected}
                onChange={() => this.props.setUseExistingNumber(false)}>
                No
              </Radio>
            </Col>
            {isExistingNumberSelected && <Col md={3}>
              <FormField
                bsSize="large"
                controlLabel="Your current number"
                required={true}
                errors={this.props.errors}
                errorKey="data[voip.existing_phone_number]"
              >
                <FormControl
                  onChange={e => this.props.setData('voip.existing_phone_number', e.target.value)}
                  type="text"
                  value={signup.data.get('voip.existing_phone_number') || ''}
                />
              </FormField>
              <FormField
                bsSize="large"
                controlLabel="Your current phone provider"
                required={true}
                errors={this.props.errors}
                errorKey="data[voip.existing_phone_provider]"
              >
                <FormControl
                  onChange={e => this.props.setData('voip.existing_phone_provider', e.target.value)}
                  type="text"
                  value={signup.data.get('voip.existing_phone_provider') || ''}
                />
              </FormField>
              <FormField
                bsSize="large"
                controlLabel="Account number with your current phone provider"
                required={true}
                errors={this.props.errors}
                errorKey="data[voip.existing_phone_account]"
              >
                <FormControl
                  onChange={e => this.props.setData('voip.existing_phone_account', e.target.value)}
                  type="text"
                  value={signup.data.get('voip.existing_phone_account') || ''}
                />
              </FormField>
            </Col>}
          </Row>
        </div>)
      }

      if (!offer.attributes.includes('price_by_extensions') && phoneHandsetOption && empty(signup.data.get(phoneHandsetOption.dataKey))) {
        renderedOptions.push(<div key="existing-phone-type">
          <h3>Phone Type</h3>
          <Row>
            <Col md={4}>
              <p>If you'll be using your own phone handset, please let us know what type it is.</p>
            </Col>
            <Col md={8}>
              <FormField
                errors={this.state.clientSideErrors}
                errorKey="voip.existing_phone_type"
              >
                <Radio
                  checked={signup.data.get('voip.existing_phone_type') === 'analogue'}
                  onChange={() => this.props.setData('voip.existing_phone_type', 'analogue')}>
                  Analogue
                </Radio>
                <Radio
                  checked={signup.data.get('voip.existing_phone_type') === 'voip'}
                  onChange={() => this.props.setData('voip.existing_phone_type', 'voip')}>
                  VOIP
                </Radio>
              </FormField>
            </Col>
          </Row>
        </div>)
      }
    }

    return <div>{renderedOptions}</div>;
  }
}

ServiceOptions.propTypes = {
  errors: Ipt.map.isRequired,
  isPersonal: Pt.bool.isRequired,
  qualificationResults: Ipt.map.isRequired,
  receiveDoValidate: Pt.func,
  setData: Pt.func.isRequired,
  setExtraOfferId: Pt.func.isRequired,
  setNeedsVoice: Pt.func.isRequired,
  setUseExistingNumber: Pt.func.isRequired,
  signupEditMeta: Ipt.map.isRequired,
  signupWithExternals: Pt.instanceOf(SignupWithExternalsRecord).isRequired,
  voipOffers: Pt.instanceOf(OfferFilterDataRecord).isRequired,
}

const mapStateToProps = state => {
  const signupWithExternals = selectEditSignupWithExternals(state, {
    sortBy: null,
  })

  const isPersonal = signupWithExternals.offer.attributes.includes('personal');
  const upgradeGroup = isPersonal ? 'VOIPP' : 'VOIP';

  return {
    errors: selectErrorsByPath(state.signupEdit),
    signupEditMeta: state.signupEdit.get('meta'),
    signupWithExternals,
    isPersonal,
    voipOffers: makeSelectOffers()(state, {filter: {upgradeGroup, attribute: isPersonal ? 'personal' : 'business'}}),
  }
}

export default connect(
  mapStateToProps,
  signupActions,
)(ServiceOptions);



// WEBPACK FOOTER //
// ./src/components/Signup/ServiceDetail/ServiceOptions.jsx